import Config from "@/core/misc/Config";
import EventBus from "@/core/services/EventBus/EventBus";
import SentryService from "@/core/services/Sentry/SentryService";
import AclService from "@/core/services/Acl/AclService";
import RpcService from "@/core/services/Rpc/RpcService";

/**
 * Обработчик критических ошибок приложения
 */
class CriticalErrorPage {

  static show(error) {
    const diagnostics = this.#collectDiagnostics();
    const stackTrace = this.#formatStackTrace(error.stack);

    const html = this.#generateHTML(error, stackTrace, diagnostics);

    document.open();
    document.write(html);
    document.close();
  }

  static #generateHTML(error, stackTrace, diagnostics) {
    return `<!DOCTYPE html>
<html lang="ru">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Application Fatal Error</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, monospace;
      background: #181818;
      color: #e0e0e0;
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 25px;
      line-height: 1.5;
    }
    .container {
      background: #1a1a1a;
      border-radius: 10px;
      border: 1px solid #3e3e42;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
      max-width: 1100px;
      width: 100%;
      overflow: hidden;
    }
    .header {
      background: #3c1e1e;
      border-bottom: 1px solid #4a2525;
      padding: 25px 30px;
      text-align: center;
    }
    .title {
      font-size: 25px;
      font-weight: 600;
      margin-bottom: 8px;
      color: #f48771;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }
    .subtitle {
      font-size: 17px;
      color: #cccccc;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }
    .content {
      padding: 25px;
    }
    .section {
      margin-bottom: 25px;
    }
    .section-title {
      font-size: 17px;
      font-weight: 600;
      color: #9cdcfe;
      margin-bottom: 15px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }
    .error-message {
      background: #2d1b1b;
      border: 1px solid #4a2525;
      border-radius: 5px;
      padding: 20px;
      font-size: 16px;
      color: #f48771;
      word-break: break-word;
      border-left: 4px solid #f48771;
    }
    .stack-container {
      position: relative;
    }
    .stack-trace {
      background: #181818;
      border: 1px solid #3e3e42;
      border-radius: 5px;
      padding: 20px;
      font-size: 14px;
      max-height: 350px;
      overflow-y: auto;
      border-left: 4px solid #569cd6;
    }
    .stack-frame {
      margin-bottom: 8px;
      line-height: 1.4;
    }
    .stack-info {
      font-size: 15px;
      margin-bottom: 3px;
    }
    .file-name {
      color: #ffffff;
      font-weight: 500;
    }
    .line-number {
      color: #d4ac0d;
      font-weight: 500;
    }
    .stack-original {
      color: #e0e0e0;
      opacity: 0.7;
      font-size: 13px;
      margin-left:10px;
    }
    .stack-trace::-webkit-scrollbar {
      width: 8px;
    }
    .stack-trace::-webkit-scrollbar-track {
      background: #1f1f1f;
    }
    .stack-trace::-webkit-scrollbar-thumb {
      background: #464647;
      border-radius: 4px;
    }
    .diagnostics {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      gap: 20px;
    }
    .column {
      background: #1f1f1f;
      border: 1px solid #3e3e42;
      border-radius: 5px;
      padding: 18px;
    }
    .column-title {
      color: #9cdcfe;
      font-size: 14px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 12px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-weight: 600;
    }
    .item {
      margin-bottom: 10px;
      padding: 8px 10px;
      background: #1a1a1a;
      border-radius: 4px;
      border-left: 3px solid #3e3e42;
    }
    .item.success {
      border-left-color: #6a8759;
    }
    .item.error {
      border-left-color: #f48771;
    }
    .label {
      font-weight: 600;
      color: #9cdcfe;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.3px;
      margin-bottom: 4px;
    }
    .value {
      font-size: 14px;
      color: #e0e0e0;
      word-break: break-word;
    }
    .value.success {
      color: #6a8759;
    }
    .value.error {
      color: #f48771;
    }
    .actions {
      display: flex;
      gap: 12px;
      margin-top: 25px;
      padding-top: 20px;
      border-top: 1px solid #3e3e42;
    }
    .btn {
      padding: 10px 20px;
      border: 1px solid #464647;
      border-radius: 5px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s;
      font-size: 14px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }
    .btn-primary {
      background: #0e639c;
      color: #ffffff;
      border-color: #1177bb;
    }
    .btn-primary:hover {
      background: #1177bb;
    }
    .btn-secondary {
      background: #383838;
      color: #cccccc;
      border-color: #464647;
    }
    .btn-secondary:hover {
      background: #464647;
    }
    .copy-btn {
      position: absolute;
      top: 8px;
      right: 8px;
      background: #383838;
      border: 1px solid #464647;
      border-radius: 4px;
      padding: 4px 8px;
      font-size: 11px;
      cursor: pointer;
      color: #cccccc;
      font-family: inherit;
    }
    .copy-btn:hover {
      background: #464647;
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1 class="title">Application Fatal Error</h1>
      <p class="subtitle">Critical initialization failure</p>
    </div>

    <div class="content">
      <div class="section">
        <h2 class="section-title">Error Message</h2>
        <div class="error-message">${this.#escapeHtml(error.message || 'Unknown error')}</div>
      </div>

      <div class="section">
        <h2 class="section-title">Stack Trace</h2>
        <div class="stack-container">
          <button class="copy-btn" onclick="copyStack()">Copy</button>
          <div class="stack-trace" id="stack">${stackTrace}</div>
        </div>
      </div>

      <div class="section">
        <h2 class="section-title">System Diagnostics</h2>
        <div class="diagnostics">
          <div class="column">
            <div class="column-title">System Services</div>
            ${this.#renderServices(diagnostics.services)}
          </div>
          <div class="column">
            <div class="column-title">Configuration</div>
            ${this.#renderConfig(diagnostics.config)}
          </div>
          <div class="column">
            <div class="column-title">Environment</div>
            ${this.#renderEnvironment(diagnostics.environment)}
          </div>
        </div>
      </div>

      <div class="actions">
        <button class="btn btn-primary" onclick="location.reload()">
          Reload Application
        </button>
        <button class="btn btn-secondary" onclick="copyReport()">
          Copy Error Report
        </button>
      </div>
    </div>
  </div>

  <script>
    function copyStack() {
      const el = document.getElementById('stack');
      navigator.clipboard.writeText(el.textContent).then(() => {
        const btn = document.querySelector('.copy-btn');
        const orig = btn.textContent;
        btn.textContent = 'Copied!';
        setTimeout(() => btn.textContent = orig, 1500);
      });
    }

    function copyReport() {
      const report = \`APPLICATION FATAL ERROR
==============================

Timestamp: \${new Date().toISOString()}
Error: ${error.message || 'Unknown error'}

STACK TRACE:
${error.stack || 'Stack trace unavailable'}

DIAGNOSTICS:
${JSON.stringify(diagnostics, null, 2)}
\`;
      navigator.clipboard.writeText(report).then(() => {
        const btn = event.target;
        const orig = btn.textContent;
        btn.textContent = 'Copied!';
        setTimeout(() => btn.textContent = orig, 1500);
      });
    }
  </script>
</body>
</html>`;
  }

  static #formatStackTrace(stack) {
    if (!stack) return '<div class="stack-frame">Stack trace unavailable</div>';

    return stack.split('\n').map(line => {
      if (!line.trim()) return '';

      // Парсим с конца: любой_текст:номер_строки:номер_символа
      const match = line.match(/^(.+):(\d+):(\d+)$/);

      if (match) {
        const [, fullPath, lineNum] = match;
        const fileName = fullPath.split('/').pop();
        return `<div class="stack-frame"><div class="stack-info"><span class="file-name">${fileName}</span>:<span class="line-number">${lineNum}</span></div><div class="stack-original">${this.#escapeHtml(line.trim())}</div></div>`;
      } else {
        // Если не матчится паттерн :число:число, показываем как есть
        return `<div class="stack-frame"><div class="stack-original">${this.#escapeHtml(line.trim())}</div></div>`;
      }
    }).filter(Boolean).join('');
  }

  static #renderServices(services) {
    return Object.entries(services).map(([name, status]) => {
      const isSuccess = status === 'initialized';
      const itemClass = isSuccess ? 'item success' : 'item error';
      const valueClass = isSuccess ? 'value success' : 'value error';

      return `<div class="${itemClass}"><div class="label">${name.replace(/_/g, ' ')}</div><div class="${valueClass}">${status}</div></div>`;
    }).join('');
  }

  static #renderConfig(config) {
    return Object.entries(config).map(([key, value]) =>
                                        `<div class="item"><div class="label">${key.replace(/_/g, ' ')}</div><div class="value">${this.#escapeHtml(String(value))}</div></div>`
    ).join('');
  }

  static #renderEnvironment(environment) {
    return Object.entries(environment).map(([key, value]) =>
                                             `<div class="item"><div class="label">${key.replace(/_/g, ' ')}</div><div class="value">${this.#escapeHtml(String(value))}</div></div>`
    ).join('');
  }

  static #collectDiagnostics() {
    const services = {};
    const config = {};
    const environment = {};

    // Services
    try {
      services.sentry_service = SentryService?.isInitialized ? 'initialized' : 'not_initialized';
    } catch (e) {
      services.sentry_service = 'error';
    }

    try {
      services.event_bus = EventBus?.isInitialized ? 'initialized' : 'not_initialized';
    } catch (e) {
      services.event_bus = 'error';
    }

    try {
      services.acl_service = AclService?.isInitialized ? 'initialized' : 'not_initialized';
    } catch (e) {
      services.acl_service = 'error';
    }

    try {
      services.rpc_service = RpcService?.isInitialized ? 'initialized' : 'not_initialized';
    } catch (e) {
      services.rpc_service = 'error';
    }

    // Config
    try {
      config.debug_mode = Config?.debug ? 'enabled' : 'disabled';
      config.environment = Config?.environment || 'unknown';
      config.api_base_url = Config?.api?.baseURL || 'unknown';
    } catch (e) {
      config.config_status = 'unavailable';
    }

    // Environment
    environment.timestamp = new Date().toISOString();

    try {
      environment.user_agent = navigator.userAgent;
      environment.platform = navigator.platform;
      environment.cookies_enabled = navigator.cookieEnabled ? 'enabled' : 'disabled';
      environment.current_url = window.location.href;
    } catch (e) {
      environment.browser_info = 'unavailable';
    }

    try {
      if (performance?.memory) {
        environment.memory_used = `${Math.round(performance.memory.usedJSHeapSize / 1024 / 1024)}MB`;
        environment.memory_limit = `${Math.round(performance.memory.jsHeapSizeLimit / 1024 / 1024)}MB`;
      }
      environment.viewport_size = `${window.innerWidth}x${window.innerHeight}`;
    } catch (e) {
      // Skip
    }

    try {
      localStorage.setItem('__test__', 'test');
      localStorage.removeItem('__test__');
      environment.local_storage = 'available';
    } catch (e) {
      environment.local_storage = 'unavailable';
    }

    return { services, config, environment };
  }

  static #escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

export default CriticalErrorPage;
